import React from "react";
import { useTranslation } from "react-i18next";
import { AlertTriangle } from "lucide-react";
import { CountInvoiceBatchScopeResponse } from "../../../models/InvoiceBatch";

interface Props {
	scopeCount?: CountInvoiceBatchScopeResponse;
	isLoading?: boolean;
}

/**
 * The projected size of an invoice run, shown before the preview is generated.
 *
 * Deliberately just the two numbers that decide whether the filter is right —
 * how many customers and how many attested time reports it lands on. Saying
 * "attested" up front is what makes the count add up, so no separate note
 * about the non-attested remainder is needed.
 */
const InvoiceBatchScopeSummary: React.FC<Props> = ({ scopeCount, isLoading }) => {
	const { t } = useTranslation();

	if (!scopeCount) return null;

	const { customers, invoiceableTimeReports } = scopeCount.counts;

	const isEmpty = invoiceableTimeReports === 0;

	return (
		<div
			className={`mb-4 rounded-md border p-3 text-sm ${
				isEmpty ? "border-amber-300 bg-amber-50" : "border-gray-200 bg-gray-50"
			} ${isLoading ? "opacity-60" : ""}`}
		>
			{isEmpty ? (
				<div className="flex items-start gap-2">
					<AlertTriangle size={16} className="mt-0.5 shrink-0 text-amber-600" />
					<div className="font-medium">{t("invoiceBatch.scope.empty")}</div>
				</div>
			) : (
				<>
					<div className="text-gray-600">{t("invoiceBatch.scope.heading")}</div>
					<div className="mt-1 font-medium">
						{t("invoiceBatch.scope.customers", { count: customers })}
					</div>
					<div className="font-medium">
						{t("invoiceBatch.scope.attestedTimeReports", {
							count: invoiceableTimeReports,
						})}
					</div>
				</>
			)}
		</div>
	);
};

export default InvoiceBatchScopeSummary;
